import { SAME_DAY_TRAVEL_BUFFER_MINUTES, type AvailabilityItem, type ReasonData, type UnavailableReason } from '@koderek/shared';
import { ApiError } from '../../api/client';
import { t } from '../../texts/pl';
import { formatDateTime, formatTime } from '../editions/format';

const r = t.enrollment.reasons;

function ageRange(min: number | null | undefined, max: number | null | undefined): string {
  if (min != null && max != null) return `${min}–${max} lat`;
  if (min != null) return `od ${min} lat`;
  if (max != null) return `do ${max} lat`;
  return 'dzieci';
}

/** Zajęcia, z którymi nie da się połączyć – z miejscem, gdy są w innej edycji tego dnia. */
function conflictLabel(c: ReasonData['conflict']): string {
  if (!c) return '';
  const label = `${c.symbol} ${c.title}, ${formatTime(c.startsAt)}–${formatTime(c.endsAt)}`;
  return c.place ? `${label} (${c.place})` : label;
}

/** US-502 AC-1: komunikat z kodu powodu i danych od serwera – interfejs nie liczy reguł (AC-3). */
export function reasonText(reason: UnavailableReason, data: ReasonData | null | undefined): string {
  const name = data?.participant ?? '';
  switch (reason) {
    case 'not_open_yet':
      return data?.opensAt ? r.not_open_yet(formatDateTime(data.opensAt)) : r.full;
    case 'other_edition':
      return r.other_edition(name, data?.otherEdition ? `${data.otherEdition.name} (${data.otherEdition.city})` : '');
    case 'age':
      return r.age(ageRange(data?.ageMin, data?.ageMax), name, data?.age);
    case 'collision':
      return r.collision(conflictLabel(data?.conflict));
    case 'travel_buffer':
      return r.travel_buffer(conflictLabel(data?.conflict), data?.bufferMinutes ?? SAME_DAY_TRAVEL_BUFFER_MINUTES);
    default:
      return r[reason];
  }
}

/** US-502 AC-2: to, co widzi gość – powody niezależne od uczestnika. */
export function publicStateText(state: AvailabilityItem['state'], opensAt: string | undefined): string | null {
  if (state === 'open') return null;
  if (state === 'not_open_yet') return opensAt ? r.not_open_yet(formatDateTime(opensAt)) : null;
  return r[state];
}

/** Powód z odrzuconego zapisu (409 `enrollment_unavailable`, details = { reason, ...data }). */
export function rejectedReason(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== 'enrollment_unavailable') return null;
  const details = error.details as ({ reason?: UnavailableReason } & ReasonData) | undefined;
  return details?.reason ? reasonText(details.reason, details) : null;
}
